import { getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { COOKIES_RESET_FOR_EXISTING_USERS_COOKIE_NAME } from "@tecumseh/Common/CookieNames";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import throttle from "lodash/throttle";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        currentLanguage: state.context.session.language,
        location,
        settings: state.context.settings,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    detailsPage?: MobiusPageProps;
    alertZoneWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],

        css: css`
            position: sticky;
            top: 0;
            z-index: 20;
            padding: 0px !important;
            max-width: 100% !important;
            background-color: ${getColor("primary")};
            .hidden {
                transition: all 2s;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 0;
                                overflow: hidden;
                            `,
                        ],
                        "min",
                    )}
            }
            > div[class^="sc"] ~ div {
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        align-items: center;
                        background-color: ${COLORS.white};
                        position: sticky;
                    `,
                    css`
                        display: flex;
                        align-items: center;
                        background-color: ${COLORS.white};
                        position: sticky;
                    `,
                    css`
                        display: flex;
                        align-items: center;
                        background-color: ${COLORS.white};
                        position: sticky;
                    `,
                    null,
                ])}
        `,
    },

    container: {
        css: css`
            min-height: 50px;
            // background-color: #112945;
            display: flex;
            max-width: 1200px;
            margin: 0 auto;
            > div:first-child {
                flex-grow: 1;
                flex-basis: 90%;
                margin-left: 0;
            }
            > div:last-child {
                max-width: 3%;
                height: auto;
                margin-right: auto;
                > div[class^="sc"] {
                    height: 50px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            display: none;
                        `,
                        null,
                        css`
                            max-width: 100px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: ${TYPOGRAPHY.size.tiny};
            padding-left: 15px;
            display: none;
        `,
    },
    alertZoneWrapper: {
        css: css`
            height: auto;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    currentLanguage,
    shellContext: { isInShell },
    location,
    settings,
}: Props) => {
    const [navbarHidden, setNavbarHidden] = useState(false);
    const firstRender = React.useRef(true);
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        firstRender.current = false;
        if (!getCookie("tecumseh-com-lang")) {
            setCookie(
                "tecumseh-com-lang",
                `${currentLanguage?.languageCode.toLowerCase() === "en-us" ? "en" : (currentLanguage?.languageCode.toLowerCase() ?? "")}`,
                {
                    expires: 365,
                    domain: settings?.properties?.cookieDomain ?? "",
                },
            );
        }

        // earlier releases wrote SetContextLanguageCode scoped to the shared cookie domain. Alongside the backend's host-only copy it left two
        // conflicting cookies, so remove the domain-scoped copy and keep only the host-only one.
        if (settings?.properties?.cookieDomain) {
            removeCookie("SetContextLanguageCode", { domain: settings.properties.cookieDomain });
        }

        if (!getCookie("SetContextLanguageCode")) {
            setCookie("SetContextLanguageCode", currentLanguage?.languageCode.toLowerCase() ?? "");
        }
    }, [currentLanguage]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        if (!getCookie("cms_CurrentContentMode")) {
            window.addEventListener("scroll", scrollHandler);
        }
        return () => window.removeEventListener("scroll", scrollHandler);
    }, []);

    React.useEffect(() => {
        if (
            !isInShell &&
            !firstRender.current &&
            currentLanguage?.languageCode?.toLowerCase() !== getCookie("SetContextLanguageCode")?.toLowerCase() &&
            getCookie(COOKIES_RESET_FOR_EXISTING_USERS_COOKIE_NAME)
        ) {
            window.location.reload();
        }
    });

    const scrollHandler = throttle(() => {
        const isDesktopHeader = window.outerWidth >= 992;
        if (isDesktopHeader) {
            if (document.body.scrollTop > 1 || document.documentElement.scrollTop > 1) {
                if (!navbarHidden) {
                    setNavbarHidden(true);
                }
            } else {
                setNavbarHidden(false);
            }
        }
    }, 100);

    useEffect(() => {
        const existing = document.querySelector('link[rel="alternate"][hreflang="x-default"]');

        const languageCode = currentLanguage?.languageCode || "";
        const defaultHref =
            languageCode && languageCode.toLowerCase() !== "en-us"
                ? `${window.location.href}?SetContextLanguageCode=${languageCode}`
                : `${window.location.href}`;

        if (existing) {
            existing.setAttribute("href", defaultHref);
            return;
        }

        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = "x-default";
        link.href = defaultHref;

        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, [location]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <StyledWrapper {...styles.alertZoneWrapper}>
                <Zone contentId={id} zoneName="AlertBanner" />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page} className="test" id="main-navbar">
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Zone contentId={id} zoneName="SecondaryNavigation" />
                    <StyledWrapper {...styles.container}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                        <Zone contentId={id} zoneName="Cart" />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withIsInShell(withHistory(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;